import React, { useEffect, useState } from 'react';
import { api, type Receipt } from '../lib/api';
import { T, sx } from '../lib/tokens';
import { useIsMobile } from '../lib/useIsMobile';
import { StatusBadge } from '../components/ui/StatusBadge';
import { VendorTile } from '../components/ui/VendorTile';
import { Button } from '../components/ui/Button';
import { X, Upload, Email, Arrow } from '../components/ui/Icons';
import { UpgradeModal } from '../components/UpgradeModal';

interface Billing {
  plan: 'free' | 'solo' | 'business' | 'pro';
  status?: string;
  usage: { lifetime: number; period: number; cap: number; overage: number };
}

function usagePillColor(billing: Billing): { bg: string; color: string; border: string } {
  const { plan, usage } = billing;
  if (plan === 'free') {
    const n = usage.lifetime;
    if (n >= 20) return { bg: T.failBg, color: T.fail, border: T.fail };
    if (n >= 15) return { bg: T.warnBg, color: T.warn, border: T.warn };
    return { bg: '#E8F5E3', color: T.sageDarker, border: T.sageDeep };
  }
  const pct = usage.cap > 0 ? usage.period / usage.cap : 0;
  if (usage.period > usage.cap) return { bg: T.failBg, color: T.fail, border: T.fail };
  if (pct >= 0.8) return { bg: T.warnBg, color: T.warn, border: T.warn };
  return { bg: '#E8F5E3', color: T.sageDarker, border: T.sageDeep };
}

function usagePillLabel(billing: Billing): string {
  if (billing.plan === 'free') {
    return `${billing.usage.lifetime} / 20 free`;
  }
  if (billing.usage.period > billing.usage.cap) {
    return `Overage: +${billing.usage.period - billing.usage.cap}`;
  }
  return `${billing.usage.period} / ${billing.usage.cap} this month`;
}

type Tab = 'all' | 'reconciled' | 'review';

const REVIEW_STATUSES = new Set(['pending', 'parsed', 'needs_review', 'confirmed', 'failed', 'duplicate']);
const RECONCILED_STATUSES = new Set(['posted', 'reconciled']);

function filterReceipts(receipts: Receipt[], tab: Tab): Receipt[] {
  if (tab === 'reconciled') return receipts.filter((r) => RECONCILED_STATUSES.has(r.status));
  if (tab === 'review') return receipts.filter((r) => REVIEW_STATUSES.has(r.status));
  return receipts;
}

function fmtDate(d: string | null): string {
  if (!d) return '--';
  const dt = new Date(d);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${dt.getDate()} ${months[dt.getMonth()]} ${dt.getFullYear()}`;
}

function fmtMoney(amount: number | null, currency: string): string {
  if (amount == null) return '--';
  return `${currency} ${amount.toFixed(2)}`;
}

function thisMonthReceipts(receipts: Receipt[]): Receipt[] {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  return receipts.filter((r) => {
    if (!r.receipt_date) return false;
    const d = new Date(r.receipt_date);
    return d.getFullYear() === y && d.getMonth() === m;
  });
}

export function Dashboard() {
  const isMobile = useIsMobile();
  const [receipts, setReceipts] = useState<Receipt[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<Tab>('all');
  const [inboxEmail, setInboxEmail] = useState('');
  const [copied, setCopied] = useState(false);
  const [billing, setBilling] = useState<Billing | null>(null);
  const [accuracy, setAccuracy] = useState<number | null>(null);
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  async function load() {
    const [r, me, b, m] = await Promise.all([
      api.listReceipts(),
      api.me(),
      api.billingStatus().catch(() => null),
      api.categorisationMetrics().catch(() => null),
    ]);
    setReceipts(r.receipts);
    setInboxEmail('receipts@receiptrec.com');
    if (b) setBilling(b as Billing);
    if (m) setAccuracy(m.accuracy);
    setLoading(false);
  }

  useEffect(() => { void load(); }, []);

  async function deleteReceipt(id: string, e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    if (!confirm('Delete this receipt?')) return;
    await api.deleteReceipt(id);
    setReceipts((prev) => prev.filter((r) => r.id !== id));
  }

  if (loading) {
    return (
      <div style={{ padding: 40, color: T.graphiteDim, fontSize: 14 }}>Loading...</div>
    );
  }

  const monthReceipts = thisMonthReceipts(receipts);
  const monthTotal = monthReceipts.reduce((s, r) => s + (r.amount ?? 0), 0);
  const reconciledCount = receipts.filter((r) => RECONCILED_STATUSES.has(r.status)).length;
  const reviewCount = receipts.filter((r) => REVIEW_STATUSES.has(r.status)).length;
  const filtered = filterReceipts(receipts, tab);

  const tabs: { key: Tab; label: string; count: number }[] = [
    { key: 'all', label: 'All', count: receipts.length },
    { key: 'reconciled', label: 'Reconciled', count: reconciledCount },
    { key: 'review', label: 'Needs review', count: reviewCount },
  ];

  function copyEmail() {
    navigator.clipboard.writeText(inboxEmail).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  const upgradeReason = billing?.plan === 'free' && billing.usage.lifetime >= 20
    ? 'Free quota reached — subscribe to keep processing receipts.'
    : billing && billing.plan !== 'free' && billing.usage.period > billing.usage.cap
      ? 'You are over your monthly included receipts — overages are billed.'
      : undefined;

  function renderAccuracyPill() {
    if (accuracy == null) return null;
    const pct = Math.round(accuracy * 100);
    const tone = pct >= 95
      ? { bg: '#E8F5E3', color: T.sageDarker, border: T.sageDeep }
      : pct >= 85
        ? { bg: T.warnBg, color: T.warn, border: T.warn }
        : { bg: T.failBg, color: T.fail, border: T.fail };
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '6px 12px',
          fontSize: 12,
          fontWeight: 600,
          fontFamily: "'Geist Mono', monospace",
          fontVariantNumeric: 'tabular-nums',
          background: tone.bg,
          color: tone.color,
          border: `1px solid ${tone.border}`,
          borderRadius: 100,
          whiteSpace: 'nowrap',
        }}
        title="30-day auto-categorisation accuracy"
      >
        {pct}% accurate
      </span>
    );
  }

  function renderUsagePill() {
    if (!billing) return null;
    const tone = usagePillColor(billing);
    const label = usagePillLabel(billing);
    return (
      <button
        onClick={() => setUpgradeOpen(true)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '6px 12px',
          fontSize: 12,
          fontWeight: 600,
          fontFamily: "'Geist Mono', monospace",
          fontVariantNumeric: 'tabular-nums',
          background: tone.bg,
          color: tone.color,
          border: `1px solid ${tone.border}`,
          borderRadius: 100,
          cursor: 'pointer',
          whiteSpace: 'nowrap',
        }}
        title="Usage — click to upgrade"
      >
        {label}
      </button>
    );
  }

  if (isMobile) {
    return (
      <div style={{ padding: '0 16px' }}>
        {/* Compact stat header */}
        <div style={{ marginBottom: 20 }}>
          <p style={{ ...sx.labelLight, marginBottom: 6 }}>This month</p>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
            <span style={{ ...sx.serif, ...sx.mono, fontSize: 32, color: T.graphite }}>
              ${monthTotal.toFixed(2)}
            </span>
            <div style={{ display: 'flex', gap: 16, alignItems: 'baseline' }}>
              <span style={{ ...sx.mono, fontSize: 14, fontWeight: 600, color: T.graphite }}>
                {reconciledCount} <span style={{ ...sx.labelLight, fontWeight: 400 }}>reconciled</span>
              </span>
              <a href="#/review" style={{ textDecoration: 'none' }}>
                <span style={{ ...sx.mono, fontSize: 14, fontWeight: 600, color: reviewCount > 0 ? T.warn : T.graphite }}>
                  {reviewCount} <span style={{ ...sx.labelLight, fontWeight: 400, color: reviewCount > 0 ? T.warn : T.graphiteDim }}>review</span>
                </span>
              </a>
            </div>
          </div>
          {(billing || accuracy != null) && (
            <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {renderUsagePill()}
              {renderAccuracyPill()}
            </div>
          )}
        </div>

        {/* Upload button */}
        <div style={{ marginBottom: 16 }}>
          <button
            onClick={() => { window.location.hash = '#/upload'; }}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              width: '100%',
              padding: '12px 0',
              background: T.sage,
              color: T.navy,
              border: 'none',
              borderRadius: 10,
              fontSize: 14,
              fontWeight: 600,
              fontFamily: "'Geist', sans-serif",
              cursor: 'pointer',
              minHeight: 44,
            }}
          >
            <Upload size={15} /> Upload
          </button>
        </div>

        {/* Horizontal scrolling chip pills */}
        <div
          style={{
            display: 'flex',
            gap: 8,
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch',
            marginBottom: 16,
            paddingBottom: 4,
          }}
        >
          {tabs.map((t) => {
            const active = tab === t.key;
            const isReview = t.key === 'review' && t.count > 0;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                style={{
                  padding: '8px 18px',
                  fontSize: 13,
                  fontWeight: 500,
                  fontFamily: "'Geist', sans-serif",
                  color: active ? '#FFFFFF' : T.graphite,
                  background: active ? T.navy : '#FFFFFF',
                  border: `1px solid ${active ? T.navy : T.paperLine}`,
                  borderRadius: 100,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  minHeight: 44,
                }}
              >
                {t.label}{' '}
                <span
                  style={{
                    ...sx.mono,
                    fontSize: 12,
                    color: isReview && !active ? T.warn : (active ? 'rgba(255,255,255,0.7)' : T.graphiteDim),
                    marginLeft: 4,
                  }}
                >
                  {t.count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Receipt cards */}
        {filtered.length === 0 ? (
          <div style={{ padding: '40px 0', textAlign: 'center', color: T.graphiteDim, fontSize: 14 }}>
            {tab === 'all' ? 'No receipts yet. Upload one to get started.' : 'Nothing here.'}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {filtered.map((r) => {
              const isReview = REVIEW_STATUSES.has(r.status);
              return (
                <a
                  key={r.id}
                  href={`#/receipts/${r.id}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: 14,
                    background: isReview ? '#FDF9EC' : '#FFFFFF',
                    border: `1px solid ${T.paperLine}`,
                    borderRadius: 14,
                    textDecoration: 'none',
                    color: 'inherit',
                    cursor: 'pointer',
                    minHeight: 44,
                  }}
                >
                  <VendorTile vendor={r.vendor ?? '?'} size={36} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 14,
                        fontWeight: 500,
                        color: T.graphite,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {r.vendor ?? '(unknown vendor)'}
                    </div>
                    <div style={{ fontSize: 12, color: T.graphiteDim }}>
                      {fmtDate(r.receipt_date)}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ ...sx.mono, fontSize: 14, fontWeight: 500, color: T.graphite }}>
                      {fmtMoney(r.amount, r.currency)}
                    </div>
                    <div style={{ marginTop: 4 }}>
                      <StatusBadge status={r.status as any} />
                    </div>
                  </div>
                </a>
              );
            })}
          </div>
        )}

        {/* Safe area padding for iOS */}
        <div style={{ height: 'env(safe-area-inset-bottom, 20px)' }} />

        <UpgradeModal open={upgradeOpen} onClose={() => setUpgradeOpen(false)} reason={upgradeReason} />
      </div>
    );
  }

  return (
    <div>
      {/* Header stats */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 32 }}>
        <div>
          <p style={{ ...sx.labelLight, marginBottom: 8 }}>This month</p>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 32 }}>
            <span style={{ ...sx.serif, ...sx.mono, fontSize: 42, color: T.graphite }}>
              ${monthTotal.toFixed(2)}
            </span>
            <div style={{ display: 'flex', gap: 24 }}>
              <div>
                <span style={{ ...sx.mono, fontSize: 20, fontWeight: 600, color: T.graphite }}>{reconciledCount}</span>
                <span style={{ ...sx.labelLight, marginLeft: 6 }}>reconciled</span>
              </div>
              <div>
                <a
                  href="#/review"
                  style={{
                    textDecoration: 'none',
                    cursor: 'pointer',
                  }}
                >
                  <span style={{ ...sx.mono, fontSize: 20, fontWeight: 600, color: reviewCount > 0 ? T.warn : T.graphite }}>
                    {reviewCount}
                  </span>
                  <span style={{ ...sx.labelLight, marginLeft: 6, color: reviewCount > 0 ? T.warn : T.graphiteDim }}>
                    to review
                  </span>
                </a>
              </div>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {renderUsagePill()}
          {renderAccuracyPill()}
          <Button variant="ghostLight" size="small" onClick={() => { window.location.hash = '#/settings'; }}>
            Settings
          </Button>
          <Button variant="sage" size="small" onClick={() => { window.location.hash = '#/upload'; }}>
            <Upload size={15} /> Upload
          </Button>
        </div>
      </div>

      {/* Tab row */}
      <div
        style={{
          display: 'flex',
          gap: 0,
          borderBottom: `1px solid ${T.paperLine}`,
          marginBottom: 16,
        }}
      >
        {tabs.map((t) => {
          const active = tab === t.key;
          const isReview = t.key === 'review' && t.count > 0;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              style={{
                padding: '10px 20px',
                fontSize: 13,
                fontWeight: active ? 600 : 400,
                fontFamily: "'Geist', sans-serif",
                color: active ? T.graphite : T.graphiteDim,
                background: 'transparent',
                border: 'none',
                borderBottom: active ? `2px solid ${T.graphite}` : '2px solid transparent',
                marginBottom: -1,
                cursor: 'pointer',
                transition: 'color 150ms ease',
              }}
            >
              {t.label}{' '}
              <span
                style={{
                  ...sx.mono,
                  fontSize: 12,
                  color: isReview ? T.warn : (active ? T.graphiteMuted : T.graphiteDim),
                  marginLeft: 4,
                }}
              >
                {t.count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Receipt list */}
      {filtered.length === 0 ? (
        <div style={{ padding: '40px 0', textAlign: 'center', color: T.graphiteDim, fontSize: 14 }}>
          {tab === 'all' ? 'No receipts yet. Upload one to get started.' : 'Nothing here.'}
        </div>
      ) : (
        <div
          style={{
            background: '#FFFFFF',
            border: `1px solid ${T.paperLine}`,
            borderRadius: 12,
            overflow: 'hidden',
          }}
        >
          {filtered.map((r, i) => {
            const isReview = REVIEW_STATUSES.has(r.status);
            return (
              <a
                key={r.id}
                href={`#/receipts/${r.id}`}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '36px 1fr 100px 120px auto 28px',
                  alignItems: 'center',
                  gap: 12,
                  padding: '12px 16px',
                  textDecoration: 'none',
                  color: 'inherit',
                  background: isReview ? '#FDF9EC' : '#FFFFFF',
                  borderTop: i > 0 ? `1px solid ${T.paperLine}` : 'none',
                  transition: 'background 100ms ease',
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = isReview ? '#FBF4DE' : T.paper;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = isReview ? '#FDF9EC' : '#FFFFFF';
                }}
              >
                <VendorTile vendor={r.vendor ?? '?'} size={36} />
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 500,
                      color: T.graphite,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {r.vendor ?? '(unknown vendor)'}
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: T.graphiteDim,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {r.description ?? ''}
                  </div>
                </div>
                <div style={{ fontSize: 13, color: T.graphiteDim }}>{fmtDate(r.receipt_date)}</div>
                <div style={{ ...sx.mono, fontSize: 14, fontWeight: 500, textAlign: 'right', color: T.graphite }}>
                  {fmtMoney(r.amount, r.currency)}
                </div>
                <StatusBadge status={r.status as any} />
                <button
                  onClick={(e) => deleteReceipt(r.id, e)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 4,
                    color: T.graphiteDim,
                    opacity: 0.4,
                    transition: 'opacity 150ms ease, color 150ms ease',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.color = T.fail; }}
                  onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.4'; e.currentTarget.style.color = T.graphiteDim; }}
                  title="Delete"
                >
                  <X size={14} />
                </button>
              </a>
            );
          })}
        </div>
      )}

      {/* Footer bar - forwarding email */}
      {inboxEmail && (
        <div
          style={{
            marginTop: 24,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '12px 16px',
            background: T.paper2,
            borderRadius: 10,
            border: `1px solid ${T.paperLine}`,
          }}
        >
          <Email size={16} color={T.graphiteDim} />
          <span style={{ fontSize: 13, color: T.graphiteDim }}>Forward receipts to</span>
          <code
            style={{
              ...sx.mono,
              fontSize: 13,
              color: T.graphite,
              background: '#FFFFFF',
              padding: '4px 10px',
              borderRadius: 6,
              border: `1px solid ${T.paperLine}`,
            }}
          >
            {inboxEmail}
          </code>
          <button
            onClick={copyEmail}
            style={{
              background: 'transparent',
              border: `1px solid ${T.paperLine}`,
              borderRadius: 6,
              padding: '4px 12px',
              fontSize: 12,
              fontWeight: 500,
              fontFamily: "'Geist', sans-serif",
              color: T.graphiteMuted,
              cursor: 'pointer',
            }}
          >
            {copied ? 'Copied!' : 'Copy'}
          </button>
        </div>
      )}

      <UpgradeModal open={upgradeOpen} onClose={() => setUpgradeOpen(false)} reason={upgradeReason} />
    </div>
  );
}
